/* eslint-disable @typescript-eslint/camelcase */
import { isBookingDependentPlan } from '../Price/bookingDependentPricing'
import { normalizeBookingPlanRateValue } from '../Price/groupBookingPlanRatesByName'
import { shouldShowBookingPlanOnPriceGrid } from './filterBookingRatesToActivePlans'
import { IItemData } from '.'

/** Plan z API Booking (room.pricing) — pola używane przy wykrywaniu „nowych” stawek w cenniku */
export interface IBookingRoomPricingPlan {
  booking_id?: string | number
  name?: string
  is_child?: number | string
  deleted?: unknown
}

export interface INewIndependentBookingPlan {
  booking_id: string | number
  name?: string
}

/**
 * Backend oznacza „nowy plan bez ustawionej prowizji” wartością liczbową `0`.
 * Skonfigurowane 0% przychodzi jako string `"0"`.
 */
export function isBookingRatePendingCommissionPlaceholder(
  value: unknown,
): boolean {
  return typeof value === 'number' && value === 0
}

function getRates(
  booking: { rates?: Record<string, unknown> } | undefined,
): Record<string, unknown> {
  const rates = booking?.rates
  return rates && typeof rates === 'object' && !Array.isArray(rates) ? rates : {}
}

function isPerPlanMode(
  booking: { active?: unknown; rates?: Record<string, unknown> } | undefined,
): boolean {
  return (
    !Boolean(Number(booking?.active)) && hasConfiguredPerPlanBookingRates(booking)
  )
}

/** Skonfigurowane stawki globalnej prowizji per plan (bez placeholderów). */
export function getConfiguredGlobalBookingPlanRates(
  booking: { rates?: Record<string, unknown> } | null | undefined,
): Record<string, number> | undefined {
  const rates = booking?.rates
  if (!rates || typeof rates !== 'object' || Array.isArray(rates)) {
    return undefined
  }

  const out: Record<string, number> = {}
  for (const [planId, value] of Object.entries(rates)) {
    if (isBookingRatePendingCommissionPlaceholder(value)) {
      continue
    }
    out[planId] = normalizeBookingPlanRateValue(value)
  }

  return Object.keys(out).length > 0 ? out : undefined
}

/** Tryb per-plan: globalna wyłączona, ale są skonfigurowane prowizje w `rates` (po imporcie pokoju). */
export function hasConfiguredPerPlanBookingRates(
  booking: { rates?: Record<string, unknown> } | undefined,
): boolean {
  const rates = booking?.rates
  if (!rates || typeof rates !== 'object' || Array.isArray(rates)) {
    return false
  }
  return Object.values(rates).some(
    value => !isBookingRatePendingCommissionPlaceholder(value),
  )
}

/** ID planów z cenami wyłącznie z Booking (brak wpisu w rates lub placeholder `0`). */
export function buildBookingOnlyPlanIds(
  bookingObjects: IItemData[] | null | undefined,
  booking: { rates?: Record<string, unknown> } | undefined,
): Set<string> {
  const set = new Set<string>()
  const rates = getRates(booking)

  for (const obj of bookingObjects || []) {
    for (const room of obj.rooms || []) {
      for (const p of getNewIndependentBookingPlans(room, rates)) {
        set.add(String(p.booking_id))
      }
    }
  }

  for (const [planId, value] of Object.entries(rates)) {
    if (isBookingRatePendingCommissionPlaceholder(value)) {
      set.add(planId)
    }
  }

  return set
}

/**
 * Czy plan bierze ceny wyłącznie z API Booking (import pokoju lub nowy plan).
 * `bookingOnlyPlanIds` — z `buildBookingOnlyPlanIds` (siatka / hook UI).
 */
export function planFetchesPricesFromBooking(
  planId: string,
  booking: { active?: unknown; rates?: Record<string, unknown> } | undefined,
  roomUsesImport: boolean,
  roomIntegration?: { active?: unknown } | null,
  bookingOnlyPlanIds?: Set<string> | null,
): boolean {
  if (roomUsesImport) return true

  const key = String(planId)
  if (bookingOnlyPlanIds?.has(key)) return true

  const rates = getRates(booking)
  if (!isPerPlanMode(booking)) {
    return (
      Object.prototype.hasOwnProperty.call(rates, key) &&
      isBookingRatePendingCommissionPlaceholder(rates[key])
    )
  }

  if (!Object.prototype.hasOwnProperty.call(rates, key)) return true
  if (isBookingRatePendingCommissionPlaceholder(rates[key])) return true
  if (String(rates[key]) === '0') {
    return !Boolean(Number(roomIntegration?.active))
  }
  return false
}

/**
 * Plany niezależne wymagające prowizji na cenniku: brak wpisu w `rates`
 * albo placeholder (liczbowe `0`).
 */
export function getIndependentPlansNeedingCommission(
  bookingRoom: { pricing?: IBookingRoomPricingPlan[] } | null | undefined,
  rates: Record<string, unknown> | null | undefined,
): INewIndependentBookingPlan[] {
  const pricing = bookingRoom && bookingRoom.pricing
  if (!pricing || !Array.isArray(pricing)) {
    return []
  }
  const r =
    rates && typeof rates === 'object' && !Array.isArray(rates) ? rates : {}

  return pricing
    .filter(p => {
      if (p == null) return false
      if (p.deleted) return false
      if (isBookingDependentPlan(p)) return false
      const bid = p.booking_id
      if (bid === undefined || bid === null || bid === '') return false
      const key = String(bid)
      if (!Object.prototype.hasOwnProperty.call(r, key)) return true
      return isBookingRatePendingCommissionPlaceholder(r[key])
    })
    .map(p => ({
      booking_id: p.booking_id as string | number,
      name: p.name,
    }))
}

/** Plany z pokoju Booking, których nie ma jeszcze w integrations.booking.rates. */
export function getNewIndependentBookingPlans(
  bookingRoom: { pricing?: IBookingRoomPricingPlan[] } | null | undefined,
  rates: Record<string, unknown> | null | undefined,
): INewIndependentBookingPlan[] {
  const pricing = bookingRoom && bookingRoom.pricing
  if (!pricing || !Array.isArray(pricing)) {
    return []
  }
  const r =
    rates && typeof rates === 'object' && !Array.isArray(rates) ? rates : {}

  return pricing
    .filter(p => {
      if (p == null) return false
      if (p.deleted) return false
      if (isBookingDependentPlan(p)) return false
      const bid = p.booking_id
      if (bid === undefined || bid === null || bid === '') return false
      const key = String(bid)
      return !Object.prototype.hasOwnProperty.call(r, key)
    })
    .map(p => ({
      booking_id: p.booking_id as string | number,
      name: p.name,
    }))
}

/**
 * Pokój podpięty z „pobierz ceny z Booking” — przy wyłączonej globalnej
 * plany nie mają lokalnej prowizji (integration.active = 0).
 */
export function isRoomMappedWithBookingPriceImport(
  roomIntegrations: Array<{ active?: unknown }> | undefined | null,
  globalBookingCommissionActive: boolean,
): boolean {
  if (globalBookingCommissionActive) return false
  const list = roomIntegrations || []
  if (list.length === 0) return false
  return list.every(i => !Boolean(Number(i.active)))
}

/** Czy plan na siatce liczy cenę z BB + prowizja (a nie wyłącznie z API Booking). */
export function shouldComputeBedBookingCommissionPrice(
  planId: string,
  booking: { active?: unknown; rates?: Record<string, unknown> } | undefined,
  globalBookingActive: boolean,
  roomUsesImport: boolean,
  roomPlanIntegration?: { active?: unknown } | null,
  bookingOnlyPlanIds?: Set<string> | null,
): boolean {
  if (
    planFetchesPricesFromBooking(
      planId,
      booking,
      roomUsesImport,
      roomPlanIntegration,
      bookingOnlyPlanIds,
    )
  ) {
    return false
  }
  if (globalBookingActive) return true
  return shouldShowBookingPlanOnPriceGrid(planId, booking)
}
